import { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { Button, Grid, Menu, MenuItem, Typography } from "@mui/material";
import Image from "mui-image";
import {
	ExpandMoreRounded, MapRounded, HomeRounded,
	AgricultureRounded, BiotechRounded, SmartToyRounded,
	Diversity3Rounded, CrisisAlertRounded, FloodRounded,
	PublicRounded, WarningRounded,
} from "@mui/icons-material";

import { labs } from "../utils/constants/index.js";
import { jwt } from "../utils/index.js";
import { checkAiEnabled } from "../api/index.js";

import Accordion from "./Accordion.js";
import Tooltip from "./Tooltip.js";
import IconRenderer from "./shared/IconRenderer.js";
import UserIndicator from "./UserIndicator.js";

const useStyles = makeStyles((theme) => ({
	sidebar: {
		height: "100%",
		position: "absolute",
		backgroundColor: theme.palette.primary.main,
		color: "white",
		overflow: "auto",
		transition: "all 0.5s ease-in-out",
		zIndex: 1000,
	},
	toggleButton: {
		position: "absolute",
		top: "0",
		right: "0",
		zIndex: 1000,
		color: "white",
		padding: "4px",
		minWidth: "30px",
		minHeight: "30px",
		transition: "transform 0.3s ease-in-out",
	},
	buttonHover: {
		"&:hover": {
			backgroundColor: "rgba(255, 255, 255, 0.1)",
			transition: "background-color 0.3s ease",
		},
	},
}));

const LogoRenderer = ({ logo, title, size = "20px", borderRadius = "8px" }) => (
	<div
		style={{
			width: size,
			height: size,
			borderRadius,
			overflow: "hidden",
			marginRight: "8px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		}}
	>
		<Image src={logo} alt={title} fit="contain" width={size} height={size} />
	</div>
);

const ButtonWithText = ({ text, icon, more, handler, classes, tooltip = "" }) => {
	const buttonContent = (
		<span key={text}>
			{!more
				&& (
					<Button
						key={text}
						className={classes.buttonHover}
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "row",
							justifyContent: "flex-start",
							padding: "8px 40px 8px 16px",
						}}
						onClick={(event) => handler(event)}
					>
						{icon && <IconRenderer icon={icon} text={text} />}
						<Typography
							align="center"
							color="white.main"
							fontSize="medium"
							ml={1}
							display="flex"
							alignItems="center"
							sx={{ textTransform: "capitalize" }}
						>
							{text}
							{more && <ExpandMoreRounded />}
						</Typography>
					</Button>
				)}
			{more && (
				<Accordion
					key={text}
					title={(
						<Grid item sx={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "flex-start" }}>
							{icon && <IconRenderer icon={icon} text={text} />}
							<Typography
								align="center"
								color="white.main"
								fontSize="medium"
								ml={1}
								display="flex"
								alignItems="center"
								sx={{ textTransform: "capitalize" }}
							>
								{text}
							</Typography>
						</Grid>
					)}
					content={(
						<Grid container flexDirection="column" width="100%">
							{more.map((el) => (
								<Button
									key={el.title}
									color="white"
									sx={{
										justifyContent: "flex-start",
										marginLeft: "20px",
										...classes.buttonHover,
									}}
									onClick={el.handler}
								>
									{el.logo && <LogoRenderer logo={el.logo} title={el.title} />}
									<Typography sx={{ textTransform: "capitalize", textAlign: "left" }}>{el.title}</Typography>
								</Button>
							))}
						</Grid>
					)}
					alwaysExpanded={false}
					titleBackground="transparent"
					expandIconColor="white"
				/>
			)}
		</span>
	);

	return tooltip ? (
		<Tooltip title={tooltip} placement="right">
			{buttonContent}
		</Tooltip>
	) : buttonContent;
};

const ButtonSimple = ({ text, icon, handler, ind, classes, tooltip = "" }) => {
	const buttonContent = (
		<Button
			key={text}
			className={classes.buttonHover}
			sx={{
				minWidth: "30px!important",
				padding: "8px",
				position: "absolute",
				top: `${ind * 40}px`,
				left: "50%",
				transform: "translateX(-50%)",
				transition: "top 0.5s ease-in-out, background-color 0.3s ease",
			}}
			onClick={(event) => handler(event)}
		>
			{icon && (typeof icon === "string"
				? <Image src={icon} alt={text} fit="contain" width="30px" />
				: <span style={{ fontSize: "30px", display: "flex", alignItems: "center", color: "white" }}>{icon}</span>)}
		</Button>
	);

	return tooltip ? (
		<Tooltip title={tooltip} placement="right">
			{buttonContent}
		</Tooltip>
	) : buttonContent;
};

const Sidebar = ({ isSmall: sidebarIsSmall, onToggleSidebar }) => {
	const [isSmall, setIsSmall] = useState(false);
	const [anchorElServices, setAnchorElServices] = useState(null);
	const [aiEnabled, setAiEnabled] = useState(false);
	const navigate = useNavigate();
	const classes = useStyles();
	const username = jwt.getUsername();

	const isMenuOpenServices = Boolean(anchorElServices);
	const handleServicesMenuOpen = (event) => setAnchorElServices(event.currentTarget);
	const handleServicesMenuClose = () => { setAnchorElServices(null); };

	useEffect(() => setIsSmall(sidebarIsSmall), [sidebarIsSmall]);

	// Check if AI Assistant is enabled on mount
	useEffect(() => {
		checkAiEnabled().then(setAiEnabled);
	}, []);

	// Create a wrapper handler
	const createNavigationHandler = (path) => () => {
		handleServicesMenuClose();
		navigate(path);
	};

	const buttons = [
		{
			icon: <HomeRounded />,
			text: "Home",
			handler: createNavigationHandler("/home"),
			tooltip: "Dashboard overview",
		},
		{
			icon: <AgricultureRounded />,
			text: "Products",
			handler: createNavigationHandler("/products"),
			tooltip: "Agricultural product data",
		},
		{
			icon: <MapRounded />,
			text: "Map",
			handler: createNavigationHandler("/Map"),
			tooltip: "Geographic visualization",
		},
		{
			icon: <CrisisAlertRounded />,
			text: "Contaminants",
			handler: createNavigationHandler("/contaminants"),
			tooltip: "Risk monitoring",
		},
		{
			icon: <FloodRounded />,
			text: "Disasters",
			handler: createNavigationHandler("/disasters"),
			tooltip: "Climate-related disaster monitoring",
		},
		{
			icon: <WarningRounded />,
			text: "Early Warning",
			handler: createNavigationHandler("/ews"),
			tooltip: "Early Warning System for crop yield",
		},
		{
			icon: <BiotechRounded />,
			text: "Living Labs",
			handler: (event) => {
				handleServicesMenuClose();
				handleServicesMenuOpen(event);
			},
			more: labs.map((lab) => ({
				logo: lab.logo,
				title: lab.title,
				handler: () => navigate(lab.path),
			})),
			tooltip: "Research partners",
		},
		{
			icon: <PublicRounded />,
			text: "FAOSTAT",
			handler: createNavigationHandler("/faostat"),
			tooltip: "FAO agricultural statistics for Europe",
		},
		{
			icon: <Diversity3Rounded />,
			text: "Social Impact",
			handler: () => {
				handleServicesMenuClose();
				navigate("/socialimpact");
			},
			tooltip: "Community metrics",
		},
		// AI Assistant is only shown if the backend has AI functionality enabled
		...(aiEnabled ? [{
			icon: <SmartToyRounded />,
			text: "AI Assistant",
			handler: () => {
				handleServicesMenuClose();
				navigate("/aiassistant");
			},
			tooltip: "Get AI-powered insights",
		}] : []),
	];

	const renderServicesMenu = (
		<Menu
			keepMounted
			anchorEl={anchorElServices}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			open={isMenuOpenServices}
			onClose={handleServicesMenuClose}
		>
			{buttons.find((button) => button.text === "Living Labs").more.map((moreButton) => (
				<MenuItem key={moreButton.title} onClick={() => { handleServicesMenuClose(); moreButton.handler(); }}>
					{moreButton.logo && <LogoRenderer logo={moreButton.logo} title={moreButton.title} />}
					<Typography sx={{ marginLeft: "5px" }}>
						{moreButton.title}
					</Typography>
				</MenuItem>
			))}
		</Menu>
	);

	const toggleSidebar = () => {
		if (window.innerWidth >= 900) {
			const newIsSmall = !isSmall;
			setIsSmall(newIsSmall);
			onToggleSidebar(newIsSmall);
		}
	};

	return (
		<div className={classes.sidebar} style={{ width: isSmall ? "50px" : "200px", padding: "20px 5px", textAlign: "center", display: "flex", flexDirection: "column", transition: "width 0.5s ease-in-out, padding 0.5s ease-in-out" }}>
			<Button
				className={classes.toggleButton}
				sx={{
					transform: isSmall ? "rotate(-90deg)" : "rotate(90deg)",
					...classes.buttonHover,
				}}
				onClick={toggleSidebar}
			>
				<ExpandMoreRounded fontSize="small" />
			</Button>
			{!isSmall && buttons.map((button) => (
				<ButtonWithText
					key={button.text}
					icon={button.icon}
					text={button.text}
					handler={button.handler}
					more={button.more}
					classes={classes}
					tooltip={button.tooltip}
				/>
			))}
			{isSmall && (
				<div style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					position: "relative",
					height: `${buttons.length * 40}px`, // Make the container tall enough
					width: "100%",
					transition: "all 0.5s ease-in-out",
				}}
				>
					{buttons.map((button, ind) => (
						<ButtonSimple
							key={button.text}
							icon={button.icon}
							text={button.text}
							handler={button.handler}
							more={button.more}
							ind={ind}
							classes={classes}
							tooltip={button.tooltip}
						/>
					))}
				</div>
			)}
			<div style={{ marginTop: "auto", paddingTop: "16px" }}>
				<UserIndicator
					username={username}
					iconOnly={isSmall}
					sx={{ justifyContent: isSmall ? "center" : "flex-start", px: isSmall ? 0 : 2, color: "white", width: "100%" }}
				/>
			</div>
			{renderServicesMenu}
		</div>
	);
};

export default Sidebar;
